import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { doGetPromise, doDeletePromise } from '../config/core.httpclient-utils';

import { AbstractControl, ValidationErrors, AsyncValidatorFn, Validators } from '@angular/forms';
import { Observable, of, from } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, map, tap } from 'rxjs/operators';

export function createUnixNameFieldDefinition(projectService: ProjectService) {
    return [
        '',
        {
            validators: [
                Validators.required,
                Validators.pattern('[a-z][a-z0-9_-]{2,14}'), // Matches the regex for valid unix names
            ],
            asyncValidators: [uniqueUnixNameValidator(projectService)], // Add the async validator here
            updateOn: 'blur'
        }
    ]
}

export function uniqueUnixNameValidator(projectService: ProjectService): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
        if (!control.value) {
            return of(null); // No validation error if the value is empty
        }

        return of(control.value).pipe(
            debounceTime(500), // Debounce to avoid excessive API calls
            tap(() => {
                // Add the `{ checking: true }` flag to the errors
                const errors = control.errors || {};
                control.setErrors({ ...errors, checking: true });
            }),
            switchMap((unixName: string) =>
                from(projectService.get(unixName)).pipe(
                    map(() => {
                        // If the API returns success, the name is not unique
                        return { notUnique: true };
                    }),
                    catchError(() => {
                        // If the API returns an error (e.g., 404), the name is unique
                        return null;
                    }),
                    finalize(() => {
                        // Remove the `{ checking: true }` flag from the errors
                        const currentErrors = control.errors || {};
                        delete currentErrors.checking;
                        control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
                    })
                )
            ),
            catchError(() => {
                // Handle unexpected errors and remove the `{ checking: true }` flag
                const currentErrors = control.errors || {};
                delete currentErrors.checking;
                control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
                return of(null);
            })
        );
    };
}
@Injectable()
export class ProjectService {

    private baseUrl = '/api/project';

    constructor(private http: HttpClient) {
        this.http = http;
    }

    /** Internal */
    getSubUrl(unixName: string, entity: string = '', id: string = '') {
        const path = [unixName, entity, id].filter(
            (s) => s && s != ''
        ).join('/');
        return `${this.baseUrl}/${path}`;
    }

    get(unixName: string, sub: string = '', queryParams: any = {}) {
        return doGetPromise(
            this.http,
            this.getSubUrl(unixName, sub),
            queryParams
        );
    }
    /** Internal */

    save(unixName: string, proj: any) {
        return this.http.post(
            this.getSubUrl(unixName),
            proj
        ).toPromise();
    }

    fetch(queryParams: any = {}) {
        return doGetPromise(
            this.http,
            this.baseUrl,
            queryParams
        );
    }

    // Alias for fetch
    search(queryParams: any = {}) {
        return this.fetch(queryParams);
    }

    activity(unixName: string, queryParams: any = {}) {
        return this.get(
            unixName, 'activity', queryParams
        );
    }

    details(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/details?:querystring',
        return this.get(unixName, 'details', queryParams);
    }

    builds(unixName: string) {
        // url: '/api/project/:unixName/builds',
        return this.get(unixName, 'builds');
    }

    members(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/members?:querystring',
        return this.get(unixName, 'members', queryParams);
    }

    joinRequests(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/joinRequests?:querystring',
        return this.get(unixName, 'joinRequests', queryParams);
    }

    join(unixName: string) {
        // url: '/api/project/:unixName/joinRequest',
        return this.http.post(
            this.getSubUrl(unixName, 'joinRequest'),
            {}  // No body for this request, just route param and auth user
        ).toPromise();
    }

    setParent(unixName: string, parentId: string) {
        // url: '/api/project/:unixName/parent',
        return this.http.post(
            this.getSubUrl(unixName, 'parent'),
            { parentId: parentId }
        ).toPromise();
    }

    setChildren(unixName: string, children: string) {
        // url: '/api/project/:unixName/children',
        return this.http.post(
            this.getSubUrl(unixName, 'children'),
            { children: children }
        ).toPromise();
    }

    invites(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/invite?:querystring',
        return this.get(unixName, 'invite', queryParams);
    }

    getParents(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/parents?:querystring',
        return this.get(unixName, 'parents', queryParams);
    }

    roles(unixName: string) {
        // url: '/api/project/:unixName/roles?rel=user_count',
        return this.get(unixName, 'roles', { rel: 'user_count' });
    }

    deleteRole(unixName: string, roleId: any) {
        // url: '/api/project/:unixName/role/:roleId',
        return doDeletePromise(
            this.http,
            this.getSubUrl(
                unixName, 'role', roleId
            )
        );
    }

    report(queryParams: any = {}) {
        // url: '/api/report?:filters',
        return doGetPromise(
            this.http,
            '/api/report',
            queryParams
        );
    }

    sprints(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/sprints?:filters',
        return this.get(unixName, 'sprints', queryParams);
    }

    sprintDetails(sprintId, queryParams: any = {}) {
        // url: '/api/sprint/:sprintId/details',
        return doGetPromise(
            this.http,
            `/api/sprint/${sprintId}/details`,
            queryParams
        );
    }

    updateSprint(sprint: any) {
        // url: '/api/sprint/:id',
        const sprintId = sprint.id ? sprint.id : 0;
        return this.http.post(
            `/api/sprint/${sprintId}`,
            sprint
        ).toPromise();
    }

    deleteSprint(sprintId) {
        // url: '/api/sprint/:id',
        return doDeletePromise(
            this.http,
            `/api/sprint/${sprintId}`,
        );
    }

    getUseCustomSplash(unixName: string) {
        // url: '/api/project/:projectId/customsplash',
        return this.get(unixName, 'customsplash');
    }

    addMember(unixName: string, userId, body: any) {
        // url: '/api/project/:unixName/member/:userId',
        return this.http.post(
            this.getSubUrl(unixName, 'member', userId),
            body
        ).toPromise();
    }

    rejectRequest(unixName: string, userId) {
        // url: '/api/project/:unixName/joinRequest/:userId',
        return doDeletePromise(
            this.http,
            this.getSubUrl(unixName, 'joinRequest', userId),
        );
    }

    removeMember(unixName: string, memberId, removeFromAll) {
        // url: '/api/project/:unixName/member/:memberId',
        return doDeletePromise(
            this.http,
            this.getSubUrl(unixName, 'member', memberId),
            { removeFromAll: removeFromAll }
        );
    }

    cancelInvitation(refId, settingName) {
        // url: '/api/setting/project/:refId/:settingName',
        return doDeletePromise(
            this.http,
            `/api/setting/project/${refId}/${settingName}`
        );
    }

    updateRoles(unixName, userId, roles) {
        // url: '/api/project/:unixName/member/:memberId',
        return this.http.put(
            this.getSubUrl(
                unixName, 'member', userId
            ),
            { roles: roles }
        ).toPromise();
    }

    delete(unixName: string) {
        // url: '/api/project/:unixName',
        return doDeletePromise(
            this.http,
            this.getSubUrl(unixName)
        );
    }

    sendInvite(unixName: string, email, role) {
        // url: '/api/project/:projectId/invite',
        return this.http.post(
            this.getSubUrl(unixName, 'invite'),
            { email: email, role: role }
        ).toPromise();
    }

    chat(unixName: string) {
        // url: '/api/project/:projectId/chat',
        return this.get(unixName, 'chat');
    }

    modules(unixName: string) {
        // url: '/api/project/:unixName/modules',
        return this.get(unixName, 'modules');
    }

    trove(unixName: string) {
        // url: '/api/project/:unixName/trove',
        return this.get(unixName, 'trove');
    }

    postTrove(unixName: string, categories: string) {
        // url: '/api/project/:unixName/trove',
        return this.http.post(
            this.getSubUrl(unixName, 'trove'),
            { categories: categories }
        ).toPromise();
    }

    saveModules(unixName: string, modules: string) {
        // url: '/api/project/:unixName/modules',
        return this.http.post(
            this.getSubUrl(unixName, 'modules'),
            { modules: modules }
        ).toPromise();
    }

    // NOTE: Different API route, can't use unixName for this one
    saveShowStandups(projectId, value) {
        value = (value == 0) ? 0 : 1;
        return this.http.post(
            `/api/setting/project/${projectId}/showStandups`,
            { settingValue: value }
        ).toPromise();
    }

    getScm(unixName: string) {
        // url: '/api/scm/:unixName',
        return doGetPromise(
            this.http,
            `/api/scm/${unixName}`
        );
    }

    postScm(unixName: string, options: any) {
        // url: '/api/scm/:unixName',
        return this.http.post(
            `/api/scm/${unixName}`,
            options
        ).toPromise();
    }

    getRoleSettingsTemplate(unixName: string) {
        // url: '/api/project/:unixName/roleSettings',
        return this.get(unixName, 'roleSettings');
    }

    saveRole(unixName: string, role: any) {
        // url: '/api/project/:unixName/role/:roleId',
        return this.http.post(
            this.getSubUrl(unixName, 'role', role.id),
            role
        ).toPromise();
    }

    getRoleSettings(unixName: string, roleId: string) {
        // url: '/api/project/:unixName/role/:roleId?rel=role_settings&:sections&page_size=999',
        return doGetPromise(
            this.http,
            this.getSubUrl(unixName, 'role', roleId),
            {
                rel: 'role_settings',
                page_size: 999
            }
        );
    }

    getSectionRoleSettings(unixName: string, sections: string) {
        // url: '/api/project/:unixName/roles?rel=role_settings&sections=:sections',
        return doGetPromise(
            this.http,
            this.getSubUrl(unixName, 'roles'),
            {
                rel: 'role_settings',
                sections: sections
            }
        );
    }

    getAcl(unixName: string) {
        // url: '/api/scm/:unixName/acl',
        return doGetPromise(
            this.http,
            `/api/scm/${unixName}/acl`
        );
    }

    postAcl(unixName: string, aclModule: any) {
        // url: '/api/scm/:unixName/acl',
        return this.http.post(
            `/api/scm/${unixName}/acl`,
            aclModule
        ).toPromise();
    }

    deleteAcl(unixName: string, moduleId: string) {
        // url: '/api/scm/:unixName/acl/:id',
        return this.http.delete(
            `/api/scm/${unixName}/acl/${moduleId}`
        ).toPromise();
    }

    getGroups(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/groups',
        return this.get(
            unixName, 'groups', queryParams
        );
    }

    postGroup(unixName: string, group: any) {
        // url: '/api/project/:unixName/group',
        return this.http.post(
            this.getSubUrl(unixName, 'group'),
            group
        ).toPromise();
    }

    deleteGroup(unixName: string, groupUnixName: string) {
        // url: '/api/project/:unixName/group/:groupUnixName',
        return this.http.delete(
            this.getSubUrl(unixName, 'group', groupUnixName)
        ).toPromise();
    }

    getGroup(unixName: string, groupId: string) {
        // url: '/api/project/:unixName/group/:group_id',
        return doGetPromise(
            this.http,
            this.getSubUrl(
                unixName, 'group', groupId
            )
        );
    }

    putGroup(unixName: string, groupId: string, group: any) {
        // url: '/api/project/:unixName/group/:groupUnixName',
        return this.http.put(
            this.getSubUrl(unixName, 'group', groupId),
            group
        ).toPromise();
    }

    getReportResults(unixName: string, queryParams: any = {}) {
        // url: '/api/project/:unixName/report',
        return this.get(
            unixName, 'report', queryParams
        );
    }

    getReportResult(unixName: string, reportId: string) {
        // url: '/api/project/:unixName/report/:id',
        return doGetPromise(
            this.http,
            this.getSubUrl(
                unixName, 'report', reportId
            )
        );
    }

    orgMembers(unixName: string, queryParams: any = {}) {
        return this.get(unixName, 'orgMembers', queryParams);
    }

    orgMemberProjects(unixName: string, userId: number) {
        return this.get(unixName, `orgMemberProjects/${userId}`);
    }

    orgProjectsAndRoles(unixName: string) {
        return this.get(unixName, 'orgProjectsAndRoles');
    }

    membershipAudit(unixName: string, queryParams: any = {}) {
        return this.get(unixName, 'membershipAudit', queryParams);
    }

    orgMembersCsvUrl(unixName: string, queryParams: any = {}) {
        const params = new URLSearchParams({ ...queryParams, download: 'csv' });
        return `${this.baseUrl}/${unixName}/orgMembers?${params.toString()}`;
    }

    membershipAuditCsvUrl(unixName: string, queryParams: any = {}) {
        const params = new URLSearchParams({ ...queryParams, download: 'csv' });
        return `${this.baseUrl}/${unixName}/membershipAudit?${params.toString()}`;
    }
}
